import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { customerMutations, customerQueries, customerQueryKeys } from '@as-react/common-queries';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PlusIcon } from '@yonderland/andes-icons';
import { Box, ButtonAlpha, Icon, LinkButton, StackBlock, TextAlpha } from '@yonderland/andes-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../../../constants/i18nConstants';
import { useDeliveryState } from '../../../../context/CheckoutDeliveryContext';
import { DeliverySheet } from '../DeliverySheet';
import { HomeDeliveryAddressCard } from './HomeDeliveryAddressCard';
export const HomeDeliveryAddressSelector = ({ selectedAddressId, openInEditMode, }) => {
    const { t } = useTranslation([I18N_NAMESPACES.checkout, I18N_NAMESPACES.general]);
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const [{ info, openSelector, deliveryCountryId }, dispatch] = useDeliveryState();
    const isOpen = openSelector === 'delivery-address';
    const { deliveryAddresses: deliveryAddressesInfo, idCountry } = info;
    const [highlightedAddress, setHighlightedAddress] = useState();
    const [editingAddressId, setEditingAddressId] = useState(openInEditMode ? selectedAddressId : undefined);
    const [creatingNewAddress, setCreatingNewAddress] = useState(false);
    const showCTA = Boolean(!editingAddressId && highlightedAddress);
    const { getDeliveryAddresses } = customerQueries({ servicesEndpoint, defaultRequestParams });
    const { addDeliveryAddressMutation, updateDeliveryAddressMutation } = customerMutations({
        servicesEndpoint,
        defaultRequestParams,
    });
    const { mutate: addDeliveryAddress, error: addDeliveryAddressError, isPending: addDeliveryAddressIsPending, } = useMutation(addDeliveryAddressMutation());
    const { mutate: updateDeliveryAddress, error: updateDeliveryAddressError, isPending: updateDeliveryAddressIsPending, } = useMutation(updateDeliveryAddressMutation());
    const queryClient = useQueryClient();
    const queryKeyPerCountry = [...customerQueryKeys.getDeliveryAddresses(), idCountry];
    const { data: deliveryAddressesData } = useQuery(Object.assign(Object.assign({}, getDeliveryAddresses({
        enabled: isOpen && !(deliveryAddressesInfo === null || deliveryAddressesInfo === void 0 ? void 0 : deliveryAddressesInfo.length),
        select: data => data === null || data === void 0 ? void 0 : data.deliveryAddresses.filter(address => address.idCountry === deliveryCountryId),
        initialData: () => {
            var _a;
            return ({
                customerAddress: (_a = info.customerDetails) === null || _a === void 0 ? void 0 : _a.address,
                deliveryAddresses: deliveryAddressesInfo,
            });
        },
    })), { queryKey: queryKeyPerCountry }));
    const addressFormat = (address) => {
        const houseNumberAdditionTranslation = address.houseNumberAddition
            ? address.idCountry === 15
                ? t(`${I18N_NAMESPACES.checkout}:checkout.delivery.addition`)
                : t(`${I18N_NAMESPACES.checkout}:checkout.delivery.bus`)
            : '';
        const bus = houseNumberAdditionTranslation && ` ${houseNumberAdditionTranslation} ${address.houseNumberAddition}`;
        const company = address.companyName ? `${address.companyName}, ` : '';
        const countyName = address.county ? ` ${address.county}` : '';
        const street = address.houseNumber ? `${address.address} ${address.houseNumber}` : address.address;
        const fullName = address.firstName && address.lastName ? `${address.firstName} ${address.lastName}, ` : '';
        const addressLine = [address.addressLine2, address.addressLine3, address.addressLine4].filter(Boolean);
        const town = address.country && ['GB', 'BFPO', 'IE', 'United Kingdom'].includes(address.country)
            ? `, ${address.city}, ${address.postCode}`
            : ` ${address.postCode} ${address.city}`;
        return `${fullName}${company}${street}${bus}${addressLine.length ? `, ${addressLine.join(', ')}` : ''}${town}${countyName}`;
    };
    const selectDeliveryAddress = () => {
        dispatch({ type: 'set_selected_delivery_address', payload: highlightedAddress });
        closeSelector();
    };
    const handleCreateNewAddress = () => {
        setHighlightedAddress(undefined);
        setEditingAddressId(undefined);
        setCreatingNewAddress(true);
    };
    const handleSubmitNewAddress = (data) => {
        addDeliveryAddress(data, {
            onSuccess: data => {
                setCreatingNewAddress(false);
                setHighlightedAddress(data);
                updateDeliveryAddresses(oldData => (Object.assign(Object.assign({}, oldData), { deliveryAddresses: [...oldData.deliveryAddresses, data] })));
            },
        });
    };
    const handleSubmitUpdateAddress = (data) => {
        updateDeliveryAddress(data, {
            onSuccess: data => {
                setEditingAddressId(undefined);
                setHighlightedAddress(data);
                updateDeliveryAddresses(oldData => (Object.assign(Object.assign({}, oldData), { deliveryAddresses: oldData.deliveryAddresses.map(address => address.deliveryAddressId === data.deliveryAddressId
                        ? data
                        : address) })));
                if (openInEditMode) {
                    dispatch({ type: 'set_selected_delivery_address', payload: data });
                    closeSelector();
                }
            },
        });
    };
    const closeSelector = () => {
        dispatch({ type: 'set_open_selector', payload: undefined });
    };
    const updateDeliveryAddresses = (callback) => queryClient.setQueryData(queryKeyPerCountry, callback);
    const handleOnClickAddress = (address) => {
        setHighlightedAddress(address);
        setEditingAddressId(undefined);
        setCreatingNewAddress(false);
    };
    const isChecked = (deliveryAddressId) => {
        if (creatingNewAddress || (!highlightedAddress && !selectedAddressId)) {
            return false;
        }
        if (!highlightedAddress) {
            return selectedAddressId === deliveryAddressId;
        }
        return (highlightedAddress === null || highlightedAddress === void 0 ? void 0 : highlightedAddress.deliveryAddressId) === deliveryAddressId;
    };
    const handleEditAddress = (addressId) => {
        setEditingAddressId(addressId);
    };
    const handeCancelUpdateAddress = () => {
        handleEditAddress(undefined);
    };
    const handelCancelNewAddress = () => {
        setCreatingNewAddress(false);
    };
    const getContent = () => (_jsxs(StackBlock, { gap: "3", children: [_jsx(TextAlpha, { variant: "headingS", children: t(`${I18N_NAMESPACES.checkout}:checkout.delivery.delivery.address.flyout.title`) }), deliveryAddressesData === null || deliveryAddressesData === void 0 ? void 0 : deliveryAddressesData.map(address => (_jsx(HomeDeliveryAddressCard, { testId: "hd_saved_address", checked: isChecked(address.deliveryAddressId), isEditing: editingAddressId === address.deliveryAddressId, isPending: updateDeliveryAddressIsPending, error: updateDeliveryAddressError, address: address, addressTitle: addressFormat(address), onEdit: handleEditAddress, onClick: handleOnClickAddress, handleSubmit: handleSubmitUpdateAddress, handleCancel: handeCancelUpdateAddress }, address.deliveryAddressId))), _jsx(Box, { paddingBlockEnd: "8", children: creatingNewAddress && !highlightedAddress ? (_jsx(HomeDeliveryAddressCard, { testId: "hd_new_address", checked: creatingNewAddress, addressTitle: t(`${I18N_NAMESPACES.checkout}:checkout.delivery.new.address.card.title`), isPending: addDeliveryAddressIsPending, error: addDeliveryAddressError, handleSubmit: handleSubmitNewAddress, handleCancel: handelCancelNewAddress })) : (_jsxs(LinkButton, { theme: "branded", variant: "secondary", size: "md", onClick: handleCreateNewAddress, testId: "hd_add_new_delivery_address", children: [_jsx(Icon, { source: PlusIcon }), t(`${I18N_NAMESPACES.general}:address.add`)] })) })] }));
    const getCTAContent = () => (_jsx(ButtonAlpha, { fullWidth: true, testId: "hd_select_delivery_address", theme: "branded", text: t(`${I18N_NAMESPACES.checkout}:checkout.delivery.delivery.address.flyout.confirm`), onClick: selectDeliveryAddress }));
    return DeliverySheet({
        isOpen,
        onClose: closeSelector,
        getContent,
        showCTA,
        getCTAContent,
    });
};
